import React, { useState } from 'react'
import Filters, { FilterGroup } from '../organisms/Filters'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import RangeSlider from '@/components/molecules/RangeSlider'
import { Card, CardContent } from '@/components/ui/card'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { formatCurrency } from '@/utils'

export interface ReferralFiltersProps {
  search: string
  onSearchChange: (value: string) => void
  status: string
  onStatusChange: (value: string) => void
  type: string
  onTypeChange: (value: string) => void
  referralType: string
  onReferralTypeChange: (value: string) => void
  apiKeyId: string
  onApiKeyChange: (value: string) => void
  dateFrom: string
  onDateFromChange: (value: string) => void
  dateTo: string
  onDateToChange: (value: string) => void
  minTrancheSize: number | undefined
  onMinTrancheSizeChange: (value: number | undefined) => void
  maxTrancheSize: number | undefined
  onMaxTrancheSizeChange: (value: number | undefined) => void
  onReset: () => void
  className?: string
  apiKeys: Array<{ key_id: string; key_name: string }>
}

const ReferralFilters: React.FC<ReferralFiltersProps> = ({
  search,
  onSearchChange,
  status,
  onStatusChange,
  type,
  onTypeChange,
  referralType,
  onReferralTypeChange,
  apiKeyId,
  onApiKeyChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  minTrancheSize,
  onMinTrancheSizeChange,
  maxTrancheSize,
  onMaxTrancheSizeChange,
  onReset,
  className,
  apiKeys
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false)
  const filterGroups: FilterGroup[] = [
    {
      id: 'status',
      label: 'Status',
      value: status,
      onChange: onStatusChange,
      options: [
        { value: '', label: 'All Statuses' },
        { value: 'PENDING', label: 'Pending' },
        { value: 'APPROVED', label: 'Approved' },
        { value: 'REJECTED', label: 'Rejected' },
        { value: 'CONTACTED', label: 'Contacted' },
        { value: 'CANCELLED', label: 'Cancelled' },
        { value: 'COMPLETED', label: 'Completed' }
      ]
    },
    {
      id: 'type',
      label: 'Type',
      value: type,
      onChange: onTypeChange,
      options: [
        { value: '', label: 'All Types' },
        { value: 'INQUIRY', label: 'Inquiry' },
        { value: 'RFQ', label: 'RFQ' }
      ]
    },
    {
      id: 'referralType',
      label: 'Referral Type',
      value: referralType,
      onChange: onReferralTypeChange,
      options: [
        { value: '', label: 'All Referral Types' },
        { value: 'BUY', label: 'Buy' },
        { value: 'SELL', label: 'Sell' }
      ]
    },
    {
      id: 'apiKey',
      label: 'API Key',
      value: apiKeyId,
      onChange: onApiKeyChange,
      options: [
        { value: '', label: 'All Keys' },
        ...apiKeys.map(key => ({
          value: key.key_id,
          label: key.key_name
        }))
      ]
    }
  ]

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Basic Filters */}
      <Filters
        searchPlaceholder="Search by project name..."
        searchValue={search}
        onSearchChange={onSearchChange}
        filterGroups={filterGroups}
        layout="horizontal"
      />

      {/* Advanced Filters Toggle */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-2"
        >
          {showAdvanced ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          Advanced Filters
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onReset}
        >
          Reset Filters
        </Button>
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Date Range */}
          <Card variant="default">
            <CardContent className="p-4">
              <div className="space-y-3">
                <label className="text-sm font-medium">Date Range</label>
                <div className="space-y-2">
                  <Input
                    type="date"
                    placeholder="From date"
                    value={dateFrom}
                    onChange={(e) => onDateFromChange(e.target.value)}
                    className="text-sm"
                  />
                  <Input
                    type="date"
                    placeholder="To date"
                    value={dateTo}
                    onChange={(e) => onDateToChange(e.target.value)}
                    className="text-sm"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tranche Size Range */}
          <RangeSlider
            min={0}
            max={10000000}
            minValue={minTrancheSize}
            maxValue={maxTrancheSize}
            onMinChange={onMinTrancheSizeChange}
            onMaxChange={onMaxTrancheSizeChange}
            label="Tranche Size Range"
            formatValue={formatCurrency}
          />
        </div>
      )}
    </div>
  )
}

export default ReferralFilters
